import { useEffect, useRef, useCallback } from 'react';

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'click',
];

/**
 * Tracks user inactivity and calls `onTimeout` after 1 hour of no activity.
 * Activity events reset the timer.
 *
 * @param onTimeout - Callback invoked when the inactivity period expires.
 * @param enabled   - When false the timer is not started (e.g. user is not logged in).
 */
export function useInactivityTimeout(onTimeout: () => void, enabled: boolean): void {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onTimeoutRef = useRef(onTimeout);

    // Keep the callback ref current without re-registering listeners
    useEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);

    const resetTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            onTimeoutRef.current();
        }, INACTIVITY_TIMEOUT_MS);
    }, []);

    useEffect(() => {
        if (!enabled) {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        // Start the initial timer
        resetTimer();

        ACTIVITY_EVENTS.forEach(event => {
            window.addEventListener(event, resetTimer, { passive: true });
        });

        return () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            ACTIVITY_EVENTS.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, [enabled, resetTimer]);
}
